import React from 'react';
import { Box, Button, Stack, Typography, Paper } from '@mui/material';
import LightbulbOutlinedIcon from '@mui/icons-material/LightbulbOutlined';
import type { AdvanceGate } from '../../../../shared/onboarding/missionTypes';
import { coerceEventListingPlainText } from '../../../../shared/utils/coerceEventListingText';

interface MissionStepCardProps {
  title: string;
  instruction: string;
  why?: string;
  tip?: string;
  advanceGate: AdvanceGate;
  onContinue: () => void;
  onShowMe: () => void;
  onFallback?: () => void;
  showFallback?: boolean;
  fallbackLabel?: string;
  targetOccluded?: boolean;
  showManualNext?: boolean;
  routeNavigating?: boolean;
  continueDisabled?: boolean;
  stepIndex: number;
  totalSteps: number;
  showProgress?: boolean;
}

export const MissionStepCard: React.FC<MissionStepCardProps> = ({
  title,
  instruction,
  why,
  tip,
  advanceGate,
  onContinue,
  onShowMe,
  onFallback,
  showFallback = false,
  fallbackLabel = 'Do this step for me',
  targetOccluded = false,
  showManualNext = false,
  routeNavigating = false,
  continueDisabled = false,
  stepIndex,
  totalSteps,
  showProgress = true,
}) => {
  const safeTitle = coerceEventListingPlainText(title, 'Step');
  const safeInstruction = coerceEventListingPlainText(instruction, '');
  const safeWhy = why ? coerceEventListingPlainText(why, '') : '';
  const safeTip = tip ? coerceEventListingPlainText(tip, '') : '';
  const safeFallbackLabel = coerceEventListingPlainText(fallbackLabel, 'Do this step for me');
  const showContinue = advanceGate.type === 'manual' || showManualNext;
  const continueLabel = routeNavigating
    ? 'Taking you there…'
    : advanceGate.type === 'manual'
      ? 'Continue'
      : 'Next';
  const isClickGate = advanceGate.type === 'click';

  return (
    <Paper elevation={0} sx={{ p: 0, bgcolor: 'transparent' }}>
      {showProgress ? (
        <Typography variant="caption" color="text.secondary">
          Step {stepIndex + 1} of {totalSteps}
        </Typography>
      ) : null}
      <Typography variant="h6" sx={{ mt: 0.5, mb: routeNavigating ? 0.5 : 1, fontWeight: 700 }}>
        {safeTitle}
      </Typography>
      {routeNavigating ? (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
          Taking you to the coordinator dashboard…
        </Typography>
      ) : null}
      <Typography variant="body2" sx={{ mb: why || tip ? 1.5 : 0 }}>
        {safeInstruction}
      </Typography>
      {safeWhy ? (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          <strong>Why:</strong> {safeWhy}
        </Typography>
      ) : null}
      {safeTip ? (
        <Stack direction="row" spacing={1} alignItems="flex-start" sx={{ mb: 1.5 }}>
          <LightbulbOutlinedIcon color="info" fontSize="small" sx={{ mt: 0.2 }} />
          <Typography variant="body2" color="text.secondary">
            {safeTip}
          </Typography>
        </Stack>
      ) : null}
      {targetOccluded && isClickGate ? (
        <Typography variant="caption" color="warning.main" sx={{ display: 'block', mb: 1 }}>
          The highlighted control may be hidden. Collapse this panel or use the actions below.
        </Typography>
      ) : null}
      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mt: 2 }}>
        {showContinue ? (
          <Button
            variant="contained"
            onClick={onContinue}
            disabled={continueDisabled || routeNavigating}
          >
            {continueLabel}
          </Button>
        ) : routeNavigating ? (
          <Typography variant="caption" color="text.secondary" sx={{ alignSelf: 'center' }}>
            Taking you to the right page…
          </Typography>
        ) : (
          <Typography variant="caption" color="text.secondary" sx={{ alignSelf: 'center' }}>
            Complete the highlighted action to continue.
          </Typography>
        )}
        <Button variant="outlined" size="small" onClick={onShowMe}>
          Show me
        </Button>
        {showFallback && onFallback ? (
          <Button variant="text" size="small" onClick={onFallback}>
            {safeFallbackLabel}
          </Button>
        ) : null}
      </Stack>
    </Paper>
  );
};

export default MissionStepCard;
